import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ShieldCheck, Copy, Trash2 } from "lucide-react";

const ROLE_LABELS = { owner: "Генеральный директор", director: "Директор", accountant: "Бухгалтер", viewer: "Наблюдатель" };
const DOC_TYPES = ["УПД", "Акт", "Договор", "Счет-фактура", "Накладная ТОРГ-12"];

function ApiKeysTab({ canManage }) {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [newKey, setNewKey] = useState(null);

  const load = () => api.get("/api-keys").then(({ data }) => setKeys(data)).catch(() => {});
  useEffect(() => {
    if (canManage) load();
  }, [canManage]);

  const create = async () => {
    if (!name.trim()) {
      toast.error("Укажите название ключа");
      return;
    }
    try {
      const { data } = await api.post("/api-keys", { name: name.trim() });
      setNewKey(data.api_key);
      setName("");
      load();
      toast.success("API-ключ создан");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Не удалось создать ключ");
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/api-keys/${id}`);
      toast.success("Ключ отозван");
      load();
    } catch {
      toast.error("Не удалось отозвать ключ");
    }
  };

  if (!canManage) {
    return (
      <div className="text-sm text-slate-400 py-10 text-center" data-testid="api-keys-no-access">
        Доступно только владельцу и директору
      </div>
    );
  }

  return (
    <div data-testid="api-keys-tab">
      <p className="text-sm text-slate-500 mb-4">
        Ключи для интеграции с 1С и другими внешними системами через REST API (заголовок <code className="bg-slate-100 px-1 rounded">X-API-Key</code>)
      </p>
      {newKey && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm" data-testid="new-api-key-banner">
          <div className="font-medium text-amber-800 mb-1">Сохраните ключ — он показывается только один раз:</div>
          <div className="flex items-center gap-2">
            <code className="bg-white border border-amber-200 rounded px-2 py-1 font-mono text-xs flex-1 overflow-x-auto" data-testid="new-api-key-value">
              {newKey}
            </code>
            <Button
              size="icon"
              variant="outline"
              onClick={() => {
                navigator.clipboard.writeText(newKey);
                toast.success("Скопировано");
              }}
              data-testid="copy-api-key-button"
            >
              <Copy className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      )}
      <div className="flex gap-2 mb-4">
        <Input
          placeholder="Название ключа (например, 1С бухгалтерия)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="api-key-name-input"
        />
        <Button onClick={create} data-testid="create-api-key-button" className="bg-slate-900 hover:bg-slate-800 shrink-0">
          Создать ключ
        </Button>
      </div>
      <div className="space-y-2">
        {keys.map((k) => (
          <div key={k.id} className="flex items-center justify-between text-sm bg-slate-50 rounded-lg px-3 py-2.5" data-testid={`api-key-row-${k.id}`}>
            <div>
              <div className="font-medium text-slate-800">{k.name}</div>
              <div className="text-xs text-slate-400 font-mono">{k.key_prefix}••••••••</div>
            </div>
            {!k.revoked ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => revoke(k.id)}
                data-testid={`revoke-api-key-button-${k.id}`}
                className="border-rose-300 text-rose-600 hover:bg-rose-50"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" />Отозвать
              </Button>
            ) : (
              <span className="text-xs text-slate-400">Отозван</span>
            )}
          </div>
        ))}
        {keys.length === 0 && <div className="text-center text-slate-400 text-xs py-6">Ключей пока нет</div>}
      </div>
    </div>
  );
}

function DistributionTab({ canManage }) {
  const [rules, setRules] = useState([]);
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({ doc_type: "any", assignee_id: "" });

  const load = () => {
    api.get("/distribution-rules").then(({ data }) => setRules(data)).catch(() => {});
    api.get("/team/members").then(({ data }) => setMembers(data)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!form.assignee_id) {
      toast.error("Выберите сотрудника");
      return;
    }
    try {
      await api.post("/distribution-rules", form);
      toast.success("Правило создано");
      setForm({ doc_type: "any", assignee_id: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Не удалось создать правило");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/distribution-rules/${id}`);
      toast.success("Правило удалено");
      load();
    } catch {
      toast.error("Не удалось удалить правило");
    }
  };

  if (!canManage) {
    return (
      <div className="text-sm text-slate-400 py-10 text-center" data-testid="distribution-no-access">
        Доступно только владельцу и директору
      </div>
    );
  }

  return (
    <div data-testid="distribution-tab">
      <p className="text-sm text-slate-500 mb-4">Входящие документы автоматически назначаются сотруднику по типу документа</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <select
          value={form.doc_type}
          onChange={(e) => setForm({ ...form, doc_type: e.target.value })}
          data-testid="distribution-type-select"
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
        >
          <option value="any">Любой тип документа</option>
          {DOC_TYPES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <select
          value={form.assignee_id}
          onChange={(e) => setForm({ ...form, assignee_id: e.target.value })}
          data-testid="distribution-assignee-select"
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
        >
          <option value="">Назначить сотруднику</option>
          {members.map((m) => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
        </select>
        <Button onClick={create} data-testid="create-distribution-rule-button" className="bg-blue-600 hover:bg-blue-700">
          Добавить правило
        </Button>
      </div>
      <div className="space-y-2">
        {rules.map((r) => (
          <div key={r.id} className="flex items-center justify-between text-sm bg-slate-50 rounded-lg px-3 py-2" data-testid={`distribution-rule-row-${r.id}`}>
            <span className="text-slate-700">
              <span className="font-medium">{r.doc_type === "any" ? "Любой тип" : r.doc_type}</span> → {r.assignee_name}
            </span>
            <Button size="icon" variant="ghost" onClick={() => remove(r.id)} data-testid={`remove-distribution-rule-button-${r.id}`}>
              <Trash2 className="w-3.5 h-3.5 text-slate-400" />
            </Button>
          </div>
        ))}
        {rules.length === 0 && (
          <div className="text-center text-slate-400 text-xs py-6">Правил пока нет — входящие документы распределяются вручную</div>
        )}
      </div>
    </div>
  );
}

export default function Settings() {
  const { user, organization } = useAuth();
  const canManage = user?.role === "owner" || user?.role === "director";

  return (
    <div data-testid="settings-page" className="max-w-2xl">
      <h1 className="text-2xl font-bold text-slate-900 mb-5">Настройки</h1>
      <Tabs defaultValue="profile">
        <TabsList className="mb-4" data-testid="settings-tabs">
          <TabsTrigger value="profile" data-testid="settings-tab-profile">Реквизиты</TabsTrigger>
          <TabsTrigger value="api" data-testid="settings-tab-api">API-ключи (1С)</TabsTrigger>
          <TabsTrigger value="distribution" data-testid="settings-tab-distribution">Распределение</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4">
            <div className="text-sm font-semibold text-slate-700 mb-3">Организация</div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-xs text-slate-400">Название</div>
                <div className="font-medium text-slate-900" data-testid="settings-org-name">{organization?.name}</div>
              </div>
              <div>
                <div className="text-xs text-slate-400">ИНН</div>
                <div className="font-medium text-slate-900 font-mono" data-testid="settings-org-inn">{organization?.inn}</div>
              </div>
              <div>
                <div className="text-xs text-slate-400">КПП</div>
                <div className="font-medium text-slate-900 font-mono" data-testid="settings-org-kpp">{organization?.kpp || "—"}</div>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="text-sm font-semibold text-slate-700 mb-3">Пользователь</div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-xs text-slate-400">Имя</div>
                <div className="font-medium text-slate-900" data-testid="settings-user-name">{user?.name}</div>
              </div>
              <div>
                <div className="text-xs text-slate-400">Email</div>
                <div className="font-medium text-slate-900" data-testid="settings-user-email">{user?.email}</div>
              </div>
              <div>
                <div className="text-xs text-slate-400">Роль</div>
                <div className="font-medium text-slate-900">{ROLE_LABELS[user?.role] || user?.role}</div>
              </div>
              <div className="flex items-center gap-1.5 text-emerald-600" data-testid="settings-user-certificate">
                <ShieldCheck className="w-4 h-4" /> Сертификат: {user?.certificate_id}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="api">
          <ApiKeysTab canManage={canManage} />
        </TabsContent>

        <TabsContent value="distribution">
          <DistributionTab canManage={canManage} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
